import { Button } from "@mui/joy";
import { Box, Divider, Paper, Typography } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridRowParams,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { DateRange } from "@mui/x-date-pickers-pro";
import ExcelJS from "exceljs";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { Moment } from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  currencyFormatter,
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { Talent_Entity } from "../../entities/talent";
import { Vtransaction_Entity } from "../../entities/vtransaction";
import { mapToArray } from "../../helpers/reducerHelpers";
import {
  earningsFromTotals,
  withinDateRange,
} from "../../helpers/talentEarnings";
import {
  JobTalentSummary,
  useGetJobTalentSummariesQuery,
} from "../../redux/job/jobSummaryEndpoints";
import { DateRangePicker } from "../dateRangePicker/dateRangePicker";
import { useTalents } from "../../redux/talent/talentHooks";

import { useAppSelector } from "../hooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import TalentLedger from "../talentLedger/talentLedger";
import { useVtransactions } from "../../redux/vtransaction/vtransactionHooks";
import { useCreateVtransactionMutation } from "../../redux/vtransaction/vtransactionEndpoints";
import { create } from "domain";

/** What a talent with no jobs in the range contributes. */
const EMPTY_EARNINGS = { earned: 0, hours: 0, avgHourlyRate: 0 };

/**
 * Earnings figures for one talent, named as the ledger row holds them.
 *
 * Hands the server's totals to the same helper the Invoices tab and the talent
 * ledger cards use rather than dividing here, so all three scale, round and
 * rate identically. The summary arrives in the units a job carries, which is
 * what makes that possible.
 *
 * @param {object} summary Server-side totals for one talent.
 * @returns {object} Earned, hours and the hourly rate.
 */
function earningsFor(summary: JobTalentSummary) {
  const { total, totalHours, avgHourlyRate } = earningsFromTotals(
    summary.hours,
    summary.total,
    summary.hourlyEarned
  );
  return { earned: total, hours: totalHours, avgHourlyRate };
}

/** One talent's line on the main ledger, for the currently selected range. */
type LedgerRow = {
  id: number;
  talentID: number;
  /** Flows, from jobs: earned and hours worked inside the range. */
  earned: number;
  hours: number;
  avgHourlyRate: number;
  /** Flows, from transactions, inside the range. */
  credited: number;
  debited: number;
  /** A position: every transaction up to the range's end. */
  balance: number;
  vtransactions: Vtransaction_Entity[];
};

/**
 * Vtransaction
 *
 * @returns {null} Vtransaction
 */
export default function Vtransactions() {
  const { vtransactions } = useVtransactions();
  const [date, setDate] = useState<DateRange<Moment>>([null, null]);
  // Hours and earnings live on jobs, and this page covers every talent. Asking
  // for the jobs themselves meant every job in the system, and GET /jobs runs a
  // query per row on top of that, so the server totals them instead: one row
  // per talent. Bounds are sent as instants because the picker works in the
  // reader's timezone and the server shouldn't have to guess it.
  const { data: jobSummaries } = useGetJobTalentSummariesQuery({
    from: date[0]?.clone().startOf("day").utc().format() ?? undefined,
    to: date[1]?.clone().endOf("day").utc().format() ?? undefined,
  });
  const { talentsMap } = useTalents();
  const [createVtransaction] = useCreateVtransactionMutation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();
  const [count, setCount] = useState(0);
  const { talents } = useTalents();
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (vtransactions?.length) {
      if (vtransactions.length > count) scrollToBottom();
      setCount(vtransactions.length);
    }
  }, [vtransactions]);

  function scrollToBottom() {
    if (vtransactions?.length && apiRef && apiRef.current) {
      try {
        apiRef.current.scrollToIndexes({
          rowIndex: vtransactions.length - 1,
          colIndex: 0,
        });
      } catch (error) {
        console.log("Unable to scroll");
      }
    }
  }

  // Credited and debited are flows, so they cover the range. Balance is a
  // position, so it counts everything up to the range's end and ignores its
  // start — netting only the window would report 0 for anyone whose history
  // predates it, which reads as "settled" rather than "quiet lately".
  //
  // Declared above handleExport deliberately: its dependency array reads both
  // of these, and a dependency array is evaluated where it is written, not
  // when the callback runs. Below the export they are still in the temporal
  // dead zone and the page throws on render.
  // The server already narrowed these to the range, so they are a flow like
  // credited and debited, and unlike the balance.
  const earningsByTalent = useMemo(() => {
    const _ret: Record<number, ReturnType<typeof earningsFor>> = {};
    (jobSummaries ?? []).forEach((summary) => {
      _ret[summary.talentID] = earningsFor(summary);
    });
    return _ret;
  }, [jobSummaries]);

  const rows = useMemo(() => {
    const _ret: Record<number, LedgerRow> = {};

    (vtransactions ?? []).forEach((vtransaction) => {
      const row = (_ret[vtransaction.talentID] ??= {
        id: vtransaction.talentID,
        talentID: vtransaction.talentID,
        ...(earningsByTalent[vtransaction.talentID] ?? EMPTY_EARNINGS),
        credited: 0,
        debited: 0,
        balance: 0,
        vtransactions: [],
      });

      if (withinDateRange(vtransaction.createdAt, date)) {
        if (vtransaction.amount > 0) row.credited += vtransaction.amount;
        else row.debited += vtransaction.amount;
        row.vtransactions.push(vtransaction);
      }

      if (withinDateRange(vtransaction.createdAt, [null, date[1]]))
        row.balance += vtransaction.amount;
    });

    return mapToArray(_ret).sort((a, b) => {
      const aLastName = talentsMap?.[a?.id]?.lastName ?? "";
      const bLastName = talentsMap?.[b?.id]?.lastName ?? "";

      return aLastName.localeCompare(bLastName);
    });
  }, [vtransactions, talentsMap, date, earningsByTalent]);

  const totals = useMemo(() => {
    const summed = rows.reduce(
      (acc, row) => ({
        earned: acc.earned + row.earned,
        hours: acc.hours + row.hours,
        credited: acc.credited + row.credited,
        debited: acc.debited + row.debited,
        balance: acc.balance + row.balance,
      }),
      { earned: 0, hours: 0, credited: 0, debited: 0, balance: 0 }
    );

    return {
      ...summed,
      // The rate across everyone's work, not the average of their rates: a
      // one-hour job at $200 shouldn't weigh the same as a 200-hour one at $50.
      avgHourlyRate: summed.hours ? summed.earned / summed.hours : 0,
    };
  }, [rows]);

  const handleExport = useCallback(async () => {
    if (isExporting || !vtransactions?.length) return;

    setIsExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Virtual Ledger");
      worksheet.columns = [
        { width: 12 }, // ID
        { width: 14 }, // Date
        { width: 28 }, // Project Name
        { width: 32 }, // Notes
        { width: 14 }, // Amount
      ];
      const exportDate = new Date();
      const formattedDate = exportDate.toISOString().split("T")[0];
      // Built from the same rows the grid renders, so the sheet can't report
      // one set of numbers while the screen shows another.
      const { credited, debited, balance } = totals;
      const rowsByTalent = new Map(rows.map((r) => [r.talentID, r]));
      const groupedTransactions = groupTransactionsByTalent(
        rows.flatMap((r) => r.vtransactions),
        talentsMap ?? {}
      );

      worksheet.addRow(["Export Date", formattedDate]);
      worksheet.addRow([
        "Date Range",
        date[0] || date[1]
          ? `${date[0]?.format("ll") ?? "start"} - ${
              date[1]?.format("ll") ?? "today"
            }`
          : "All time",
      ]);
      const creditedRow = worksheet.addRow([
        "Credited",
        currencyFormatter.format(credited / 10000),
      ]);
      const debitedRow = worksheet.addRow([
        "Debited",
        currencyFormatter.format(debited / 10000),
      ]);
      const balanceRow = worksheet.addRow([
        "Balance",
        currencyFormatter.format(balance / 10000),
      ]);
      worksheet.addRow(["Earned", currencyFormatter.format(totals.earned)]);
      worksheet.addRow(["Hours", totals.hours]);
      worksheet.addRow([
        "Avg Rate",
        `${currencyFormatter.format(totals.avgHourlyRate)}/h`,
      ]);
      worksheet.addRow([]);

      // Center the summary totals instead of the default left alignment
      [creditedRow, debitedRow, balanceRow].forEach((row) => {
        row.getCell(2).alignment = { vertical: "middle", horizontal: "center" };
      });

      groupedTransactions.forEach(({ talentID, talentName, transactions }) => {
        // The row's own figures, not a re-derivation from the transactions
        // listed below: those cover the range, the balance covers everything up
        // to its end, and hours don't come from transactions at all.
        const talentRow = rowsByTalent.get(talentID);
        const balanceForTalent = talentRow?.balance ?? 0;
        const nameRow = worksheet.addRow([
          talentName,
          `Earned ${currencyFormatter.format(talentRow?.earned ?? 0)}`,
          `${talentRow?.hours ?? 0} h @ ${currencyFormatter.format(
            talentRow?.avgHourlyRate ?? 0
          )}/h`,
          "Balance",
          currencyFormatter.format(balanceForTalent / 10000),
        ]);
        nameRow.font = { bold: true };
        const balanceCell = nameRow.getCell(5);
        balanceCell.font = {
          ...(balanceCell.font || {}),
          color:
            balanceForTalent < 0
              ? { argb: "FFB00020" }
              : balanceForTalent > 0
              ? { argb: "FF1B5E20" }
              : undefined,
        };
        balanceCell.alignment = { vertical: "middle", horizontal: "center" };

        const headerRow = worksheet.addRow([
          "ID",
          "Date",
          "Project Name",
          "Notes",
          "Amount",
        ]);
        headerRow.font = { bold: true };

        transactions
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          .forEach((transaction) => {
            worksheet.addRow([
              transaction.id,
              formatDateForExport(transaction.createdAt),
              transaction.projectName,
              transaction.notes,
              currencyFormatter.format(transaction.amount / 10000),
            ]);
          });

        worksheet.addRow([]);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `FILMS-virtual-ledger-${formattedDate}.xlsx`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Unable to export ledger", error);
    } finally {
      setIsExporting(false);
    }
    // `rows`, `totals` and `date` all feed the sheet. Leaving `date` out here
    // exports the previously selected range — exhaustive-deps isn't configured
    // in this repo, so nothing would flag it.
  }, [isExporting, talentsMap, rows, totals, date]);

  const getDetailPanelContent = useCallback(
    (
      p: GridRowParams<{
        id: number;
        talentID: number;
        vtransactions: Vtransaction_Entity[];
        balance: number;
      }>
    ) => {
      return (
        <Box
          sx={{
            flex: 1,
            height: "100%",
            p: 1,
            display: "flex",
            background: "rgba(33,150,243, 0.08)",
          }}
        >
          <TalentLedger talentID={p.row.talentID} dateRange={date} />
        </Box>
      );
    },
    [vtransactions, date]
  );

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        getCellClassName={(c) => (c.field === "id" ? "cellReadOnly" : "")}
        apiRef={apiRef}
        rows={rows}
        columns={vtransactionColumns(dispatch, confirm, talents ?? [])}
        getDetailPanelHeight={(p) => {
          return 400;
        }}
        showToolbar
        slots={{
          toolbar: VtransactionToolbar,
          footer: VtransactionFooter,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          footer: { totals, date } as any,
          toolbar: {
            onExport: handleExport,
            isExporting,
            date,
            onDateChange: setDate,
          } as any,
          cell: { tabIndex: 1 },
        }}
        getDetailPanelContent={getDetailPanelContent}
      />
    </Paper>
  );
}

function VtransactionFooter({
  totals,
  date,
}: {
  totals: {
    earned: number;
    hours: number;
    avgHourlyRate: number;
    credited: number;
    debited: number;
    balance: number;
  };
  date: DateRange<Moment>;
}) {
  const { earned, hours, avgHourlyRate, credited, debited, balance } = totals;
  const inRange = date[0] || date[1] ? " (in range)" : "";
  const asOf = date[1] ? ` (as of ${date[1].format("ll")})` : "";

  return (
    <>
      <Divider />
      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 4,
          justifyContent: "flex-end",
          fontWeight: 600,
        }}
      >
        {/* Earned and hours come from jobs; credited, debited and balance from
            the transactions. Kept apart so the two aren't read as one running
            total. */}
        <Box sx={{ textAlign: "right" }}>
          Earned{inRange}: {currencyFormatter.format(earned)}
          <br />
          Hours{inRange}: {hours}
          <br />
          Avg Rate{inRange}:{" "}
          {hours ? `${currencyFormatter.format(avgHourlyRate)}/h` : "—"}
        </Box>
        <Box sx={{ textAlign: "right" }}>
          Credited{inRange}: {currencyFormatter.format(credited / 10000)}
          <br />
          Debited{inRange}: {currencyFormatter.format(debited / 10000)}
          <br />
          <Typography variant="body1">
            Balance{asOf}: {currencyFormatter.format(balance / 10000)}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

function VtransactionToolbar({
  onExport,
  isExporting = false,
  date,
  onDateChange,
}: {
  onExport: () => void;
  isExporting?: boolean;
  date: DateRange<Moment>;
  onDateChange: (d: DateRange<Moment>) => void;
}) {
  const [createVtransaction] = useCreateVtransactionMutation();

  const handleNewVtransaction = () => {
    const newVtransaction = {
      talentID: 0,
      projectID: 0,
      amount: 0,
      id: -1,
      createdAt: "",
      projectName: "",
      updatedAt: "",
      notes: "",
    };
    createVtransaction(newVtransaction);
  };

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          width: "100%",
        }}
      >
        <Button variant="soft" size="sm" onClick={handleNewVtransaction}>
          + New Transaction
        </Button>
        <Divider
          sx={{ margin: 1, width: 2 }}
          orientation="vertical"
          variant="middle"
          flexItem
        />
        <DateRangePicker
          date={date}
          onChange={onDateChange}
          emptyLabel="All time"
        />
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
      </Box>
      <Button
        variant="plain"
        size="sm"
        onClick={onExport}
        endDecorator={<i className="fa-solid fa-arrow-down-to-line" />}
        disabled={isExporting}
      >
        Export
      </Button>
    </Toolbar>
  );
}

const vtransactionColumns = (
  dispatch: Dispatch,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  talents: Talent_Entity[]
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      type: "string",
      width: 40,
    },
    {
      field: "talentID",
      headerName: "Talent",
      type: "string",
      renderCell: (p: any) => {
        const talent = talents.find((t) => t.id === p.row.talentID);
        return (
          <Typography variant="body2">
            {talent?.firstName} {talent?.lastName}
          </Typography>
        );
      },
      flex: 1,
    },
    {
      field: "earned",
      headerName: "Earned",
      editable: false,
      type: "number",
      width: 120,
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
    {
      field: "hours",
      headerName: "Hours",
      editable: false,
      type: "number",
      width: 90,
    },
    {
      field: "avgHourlyRate",
      headerName: "Avg Rate",
      editable: false,
      type: "number",
      width: 120,
      // No hours logged is no hourly basis, not a rate of nothing. Formatting
      // the zero as "$0.00/h" reads as "paid nothing an hour" next to earnings
      // in the tens of thousands.
      valueFormatter: (valueOrParams: any, row: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        const hours = row?.hours ?? valueOrParams?.row?.hours;
        return hours ? `${currencyFormatter.format(value)}/h` : "—";
      },
    },
    {
      field: "credited",
      headerName: "Credited",
      editable: false,
      type: "number",
      width: 130,
      valueGetter: getterDivider("credited", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
    {
      field: "debited",
      headerName: "Debited",
      editable: false,
      type: "number",
      width: 130,
      valueGetter: getterDivider("debited", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
    {
      field: "balance",
      headerName: "Balance",
      editable: false,
      type: "number",
      valueGetter: getterDivider("balance", 10000),
      valueSetter: setterDivider("balance", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
  ]);

function groupTransactionsByTalent(
  vtransactions: Vtransaction_Entity[],
  talentsMap: Record<number, Talent_Entity | undefined>
) {
  const grouped = vtransactions.reduce((acc, vtransaction) => {
    if (!acc[vtransaction.talentID]) {
      const talent = talentsMap?.[vtransaction.talentID];
      acc[vtransaction.talentID] = {
        talentID: vtransaction.talentID,
        talentName: talent
          ? `${talent.firstName} ${talent.lastName}`
          : `Talent ${vtransaction.talentID}`,
        lastName: talent?.lastName ?? "",
        transactions: [],
      };
    }

    acc[vtransaction.talentID].transactions.push(vtransaction);
    return acc;
  }, {} as Record<number, { talentID: number; talentName: string; lastName: string; transactions: Vtransaction_Entity[] }>);

  return Object.values(grouped).sort(
    (a, b) =>
      a.lastName.localeCompare(b.lastName) ||
      a.talentName.localeCompare(b.talentName)
  );
}

function formatDateForExport(dateString: string) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString();
}
